import {
  useRef,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';

import { useCallbackRef } from '../../../../core/hooks/useCallbackRef.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { useResizeObserver } from '../../../../core/hooks/useResizeObserver.js';
import { useSafeLayoutEffect } from '../../../../core/hooks/useSafeLayoutEffect.js';

interface UseBreakpointCollapseArgs {
  /** Controlled / uncontrolled props for the collapsed state. */
  collapsed: {
    value: boolean | undefined;
    defaultValue: boolean;
    onChange: ((value: boolean) => void) | undefined;
  };
  /** Container width below which the panel renders as a drawer. */
  breakpoint: number;
  /** Container element observed for size changes. */
  containerRef: RefObject<HTMLDivElement | null>;
  /**
   * Fired on the first measurement and whenever the below-breakpoint state
   * changes afterwards, whether from a resize or a new `breakpoint`.
   */
  onBreakpointTransition?: (belowBreakpoint: boolean) => void;
  /**
   * When `true`, an *uncontrolled* panel auto-collapses below the breakpoint
   * and reopens above it (the Sidebar's responsive default). Details passes
   * `false`: it never auto-collapses and honors `defaultCollapsed` everywhere.
   */
  autoCollapse: boolean;
}

interface UseBreakpointCollapseResult {
  /** Most recent measured container width (`0` until first measurement). */
  containerWidth: number;
  /** Current collapsed state (controlled value if provided). */
  collapsed: boolean;
  /** Setter for the collapsed state. */
  setCollapsed: Dispatch<SetStateAction<boolean>>;
  /** True once measured and the container is narrower than `breakpoint`. */
  isBelowBreakpoint: boolean;
}

/**
 * Tracks the container width and owns the breakpoint side derived from it. The
 * breakpoint half of `usePanelState`. `onBreakpointTransition` fires on the
 * first measurement and on every subsequent change of side, from a resize or
 * from a new `breakpoint` alike. A change of side never touches a controlled
 * `collapsed`; with `autoCollapse` (the Sidebar) an uncontrolled panel
 * additionally auto-collapses below the breakpoint and reopens above it.
 *
 * @internal
 */
export function useBreakpointCollapse({
  collapsed: collapsedProps,
  breakpoint,
  containerRef,
  onBreakpointTransition,
  autoCollapse,
}: UseBreakpointCollapseArgs): UseBreakpointCollapseResult {
  const [containerWidth, setContainerWidth] = useState(0);

  const [collapsed, setCollapsed] = useControllableState({
    value: collapsedProps.value,
    defaultValue: collapsedProps.defaultValue,
    onChange: collapsedProps.onChange,
  });

  const isControlled = collapsedProps.value !== undefined;
  const emitBreakpointTransition = useCallbackRef(onBreakpointTransition);

  const isBelowBreakpoint = containerWidth !== 0 && containerWidth < breakpoint;

  // The observer's only job is to record the width; every reaction to the
  // breakpoint side is derived from `isBelowBreakpoint`, so a `breakpoint` prop
  // change and a container resize are handled identically.
  useResizeObserver({
    ref: containerRef,
    onResize: (size) => {
      if (size.width !== undefined) {
        setContainerWidth(size.width);
      }
    },
  });

  // Which side of the breakpoint the last handled change landed on. `null`
  // until the first measurement, so we can distinguish "first ever
  // measurement" from "stayed on the same side".
  const handledBelowBreakpointRef = useRef<boolean | null>(null);

  // A layout effect rather than a during-render adjustment: `setCollapsed`
  // calls the consumer's `onCollapsedChange` synchronously, which must not
  // happen mid-render. Running before paint keeps the drawer from ever being
  // painted open on a crossing.
  useSafeLayoutEffect(() => {
    if (containerWidth === 0) {
      return;
    }

    const handledBelowBreakpoint = handledBelowBreakpointRef.current;
    if (handledBelowBreakpoint === isBelowBreakpoint) {
      return;
    }
    handledBelowBreakpointRef.current = isBelowBreakpoint;

    emitBreakpointTransition(isBelowBreakpoint);

    // Only an uncontrolled Sidebar auto-adjusts `collapsed`; controlled panels
    // and Details are left to the consumer.
    if (!autoCollapse || isControlled) {
      return;
    }

    if (handledBelowBreakpoint === null) {
      // First measurement: collapse an uncontrolled Sidebar that starts narrow
      // so the nav never covers the content, but never expand it.
      if (isBelowBreakpoint && !collapsed) {
        setCollapsed(true);
      }
      return;
    }

    setCollapsed(isBelowBreakpoint);
  }, [
    autoCollapse,
    collapsed,
    containerWidth,
    emitBreakpointTransition,
    isBelowBreakpoint,
    isControlled,
    setCollapsed,
  ]);

  return { containerWidth, collapsed, setCollapsed, isBelowBreakpoint };
}
